import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ArrowRight, Lock, Building2 } from "lucide-react";

import AutheraLogo from "@/components/AutheraLogo";
import { useAuth, formatApiErrorDetail } from "@/lib/auth";

/**
 * Access Authera — dual-mode entry screen.
 *
 * Two modes behind a tabbed UI: (a) Retailer Access (password-less Retailer
 * ID lookup; MVP only), and (b) Admin Access (email + password). Both use
 * the same HttpOnly cookie; only one session at a time per browser.
 */
export default function Login() {
  const { login, retailerLogin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mode, setMode] = useState("retailer"); // "retailer" | "admin"

  const redirect = location.state?.from || "/retailer";

  return (
    <div
      data-testid="login-page"
      className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-4 sm:px-6 lg:px-8 py-12 bg-gray-50/40"
    >
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link
            to="/"
            data-testid="login-brand"
            className="inline-flex items-center gap-2.5 group"
          >
            <AutheraLogo size="md" testid="login-brand-logo" />
            <span className="font-display font-bold text-xl tracking-tight text-gray-900">
              Authera<span className="text-[#0055FF]">.</span>
            </span>
          </Link>
        </div>

        <div className="bg-white border border-gray-200 rounded-2xl shadow-[0_12px_32px_rgba(0,0,0,0.05)] overflow-hidden">
          <div className="h-[3px] bg-gradient-to-r from-transparent via-[#0055FF] to-transparent" />

          <div className="px-7 pt-7 sm:px-9">
            <div className="flex items-center gap-2 text-[10px] font-semibold tracking-[0.18em] uppercase text-[#0055FF]">
              <Lock size={12} />
              Access Authera
            </div>
            <h1 className="font-display font-bold tracking-tight text-gray-900 text-2xl mt-2">
              Sign in to your dashboard
            </h1>
          </div>

          {/* Tabs */}
          <div className="mt-6 px-7 sm:px-9">
            <div className="inline-flex bg-gray-100 p-1 rounded-lg">
              <button
                type="button"
                data-testid="login-tab-retailer"
                onClick={() => setMode("retailer")}
                className={`px-4 py-1.5 rounded-md text-xs font-semibold transition ${
                  mode === "retailer"
                    ? "bg-white text-gray-900 shadow-sm"
                    : "text-gray-600 hover:text-gray-900"
                }`}
              >
                Retailer Access
              </button>
              <button
                type="button"
                data-testid="login-tab-admin"
                onClick={() => setMode("admin")}
                className={`px-4 py-1.5 rounded-md text-xs font-semibold transition ${
                  mode === "admin"
                    ? "bg-white text-gray-900 shadow-sm"
                    : "text-gray-600 hover:text-gray-900"
                }`}
              >
                Admin Access
              </button>
            </div>
          </div>

          {mode === "retailer" ? (
            <RetailerForm
              onLogin={retailerLogin}
              afterLogin={() => navigate(redirect, { replace: true })}
            />
          ) : (
            <AdminForm
              onLogin={login}
              afterLogin={() => navigate(redirect, { replace: true })}
            />
          )}

          <div className="px-7 pb-8 sm:px-9 text-[11px] text-gray-500 text-center leading-relaxed">
            Public verification is available without sign-in at{" "}
            <Link to="/verify" className="text-[#0055FF] font-medium hover:underline">
              /verify
            </Link>
            .
          </div>
        </div>
      </div>
    </div>
  );
}

function RetailerForm({ onLogin, afterLogin }) {
  const [retailerId, setRetailerId] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await onLogin(retailerId.trim().toLowerCase());
      afterLogin();
    } catch (e) {
      setError(
        formatApiErrorDetail(e?.response?.data?.detail) ||
          "Retailer not found or inactive.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={onSubmit} className="px-7 pt-6 pb-5 sm:px-9">
      <p className="text-xs text-gray-500 leading-relaxed mb-5">
        Enter your retailer ID to access your Authera dashboard.
      </p>
      <label
        htmlFor="retailer-id"
        className="block text-[10px] font-semibold tracking-[0.18em] uppercase text-gray-600 mb-1.5"
      >
        Retailer ID
      </label>
      <div className="relative">
        <Building2
          size={14}
          className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
        />
        <input
          id="retailer-id"
          data-testid="retailer-id-input"
          type="text"
          autoComplete="organization"
          autoFocus
          required
          value={retailerId}
          onChange={(e) => setRetailerId(e.target.value)}
          placeholder="Enter your retailer ID"
          className="w-full rounded-md border border-gray-300 pl-9 pr-3 py-2.5 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#0055FF]/30 focus:border-[#0055FF]"
        />
      </div>

      {error && (
        <div
          data-testid="login-error"
          className="mt-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800"
        >
          {error}
        </div>
      )}

      <button
        type="submit"
        data-testid="retailer-submit"
        disabled={submitting}
        className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-[#0055FF] text-white px-4 py-2.5 rounded-md text-sm font-semibold hover:bg-[#0044CC] transition disabled:opacity-60 disabled:cursor-wait"
      >
        {submitting ? "Signing in…" : "Enter Dashboard"}
        <ArrowRight size={14} />
      </button>
    </form>
  );
}

function AdminForm({ onLogin, afterLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await onLogin(email.trim(), password);
      afterLogin();
    } catch (e) {
      setError(
        formatApiErrorDetail(e?.response?.data?.detail) || "Invalid login",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={onSubmit} className="px-7 pt-6 pb-5 sm:px-9">
      <p className="text-xs text-gray-500 leading-relaxed mb-5">
        Admin access only.
      </p>
      <div className="space-y-4">
        <div>
          <label
            htmlFor="login-email"
            className="block text-[10px] font-semibold tracking-[0.18em] uppercase text-gray-600 mb-1.5"
          >
            Email
          </label>
          <input
            id="login-email"
            data-testid="login-email"
            type="email"
            autoComplete="email"
            autoFocus
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full rounded-md border border-gray-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0055FF]/30 focus:border-[#0055FF]"
            placeholder="Enter admin email"
          />
        </div>
        <div>
          <label
            htmlFor="login-password"
            className="block text-[10px] font-semibold tracking-[0.18em] uppercase text-gray-600 mb-1.5"
          >
            Admin Password
          </label>
          <input
            id="login-password"
            data-testid="login-password"
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full rounded-md border border-gray-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0055FF]/30 focus:border-[#0055FF]"
          />
        </div>
      </div>

      {error && (
        <div
          data-testid="login-error"
          className="mt-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800"
        >
          {error}
        </div>
      )}

      <button
        type="submit"
        data-testid="login-submit"
        disabled={submitting}
        className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-[#0055FF] text-white px-4 py-2.5 rounded-md text-sm font-semibold hover:bg-[#0044CC] transition disabled:opacity-60 disabled:cursor-wait"
      >
        {submitting ? "Signing in…" : "Enter Admin"}
        <ArrowRight size={14} />
      </button>
    </form>
  );
}
